"use client";

import { useState } from "react";
import Link from "next/link";
import Breadcrumbs from "@/components/Breadcrumbs";
import { HowToSchemaScript } from "@/lib/schema";

export default function MeasuringGuidePage() {
  const [mountType, setMountType] = useState<'inside' | 'outside'>('inside');

  const howToData = {
    name: "How to Measure Windows for Blinds and Shades",
    description: "Quick guide to measure your windows for custom blinds. Learn inside mount vs outside mount measurements.",
    totalTime: "PT10M",
    tool: ["Steel tape measure", "Pencil", "Paper"],
    steps: [
      { name: "Choose Mount Type", text: "Inside mount fits within the frame. Outside mount covers the frame from the wall." },
      { name: "Measure Width", text: "For inside mount: measure at top, middle, bottom — use the smallest. For outside: add 3-4 inches per side." },
      { name: "Measure Height", text: "For inside mount: measure left, center, right — use the smallest. For outside: measure from mounting point to desired bottom." },
      { name: "Double-Check", text: "Measure everything twice to the nearest 1/8 inch." },
    ],
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <HowToSchemaScript data={howToData} />

      <Breadcrumbs
        items={[
          { name: 'Guides', url: '/guides' },
          { name: 'How to Measure', url: '/guides/measuring' }
        ]}
        className="mb-6"
      />

      {/* Hero */}
      <div className="text-center mb-10">
        <h1 className="text-3xl md:text-4xl font-bold mb-3">
          How to Measure for Blinds & Shades
        </h1>
        <p className="text-gray-600 text-lg">
          Grab a steel tape measure and follow these simple steps.
        </p>
      </div>

      {/* What You Need - Single Line */}
      <div className="flex items-center justify-center gap-6 mb-10 text-sm text-gray-600 bg-gray-50 rounded-lg py-3 px-4">
        <span className="flex items-center gap-1.5">
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" /></svg>
          Steel tape measure
        </span>
        <span className="text-gray-300">|</span>
        <span className="flex items-center gap-1.5">
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
          Pencil & paper
        </span>
        <span className="text-gray-300">|</span>
        <span className="flex items-center gap-1.5">
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          ~10 minutes
        </span>
      </div>

      {/* Mount Type Tabs */}
      <div className="mb-8">
        <div className="flex rounded-lg overflow-hidden border border-gray-200 mb-6">
          <button
            onClick={() => setMountType('inside')}
            className={`flex-1 py-4 px-6 text-center font-semibold transition-colors ${
              mountType === 'inside'
                ? 'bg-primary-red text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <span className="block text-lg">Inside Mount</span>
            <span className={`block text-xs mt-0.5 ${mountType === 'inside' ? 'text-blue-100' : 'text-gray-400'}`}>
              Fits inside the window frame
            </span>
          </button>
          <button
            onClick={() => setMountType('outside')}
            className={`flex-1 py-4 px-6 text-center font-semibold transition-colors ${
              mountType === 'outside'
                ? 'bg-rose-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <span className="block text-lg">Outside Mount</span>
            <span className={`block text-xs mt-0.5 ${mountType === 'outside' ? 'text-rose-100' : 'text-gray-400'}`}>
              Covers outside the window frame
            </span>
          </button>
        </div>

        {/* Inside Mount Content */}
        {mountType === 'inside' && (
          <div className="space-y-6">
            {/* When to use */}
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-5">
              <h3 className="font-semibold text-primary-dark mb-2">Best for</h3>
              <p className="text-primary-dark text-sm">
                Windows with decorative trim you want to show off. Requires at least 2.5" frame depth.
              </p>
            </div>

            {/* Measurement Image */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-center mb-4">How to Measure — Inside Mount</h3>
              <img
                src="/images/guides/inside-outside-mount.jpg"
                alt="Inside mount measurement guide — black lines show width, red lines show height"
                className="w-full max-w-4xl mx-auto h-auto rounded-lg mb-4"
              />
              <div className="flex justify-center gap-6 mb-4">
                <span className="flex items-center gap-2 text-sm">
                  <span className="w-8 h-0.5 bg-black inline-block"></span>
                  <strong>Black lines</strong> = Width
                </span>
                <span className="flex items-center gap-2 text-sm">
                  <span className="w-0.5 h-5 bg-red-500 inline-block"></span>
                  <strong className="text-red-600">Red lines</strong> = Height
                </span>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 text-sm text-primary-dark">
                <p className="font-semibold mb-1">The smallest measurement wins:</p>
                <ul className="space-y-1 ml-4 list-disc">
                  <li>The <strong>lowest value among the black lines</strong> (top, middle, bottom) is your <strong>blind width</strong></li>
                  <li>The <strong>lowest value among the red lines</strong> (left, center, right) is your <strong>blind height</strong></li>
                </ul>
              </div>
            </div>

            {/* Steps */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Width */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-8 h-8 bg-primary-red text-white rounded-full flex items-center justify-center font-bold text-sm">W</span>
                  <h3 className="text-lg font-bold">Width <span className="text-sm font-normal text-gray-500">(black lines)</span></h3>
                </div>
                <ol className="space-y-3 text-sm">
                  <li className="flex gap-3">
                    <span className="font-bold text-primary-red shrink-0">1.</span>
                    <span>Measure inside width at <strong>top, middle, and bottom</strong></span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-bold text-primary-red shrink-0">2.</span>
                    <span>Record the <strong>smallest</strong> of the three</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-bold text-primary-red shrink-0">3.</span>
                    <span>Measure to the nearest <strong>1/8&quot;</strong></span>
                  </li>
                </ol>
              </div>

              {/* Height */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-8 h-8 bg-primary-red text-white rounded-full flex items-center justify-center font-bold text-sm">H</span>
                  <h3 className="text-lg font-bold">Height <span className="text-sm font-normal text-gray-500">(red lines)</span></h3>
                </div>
                <ol className="space-y-3 text-sm">
                  <li className="flex gap-3">
                    <span className="font-bold text-primary-red shrink-0">1.</span>
                    <span>Measure inside height at <strong>left, center, and right</strong></span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-bold text-primary-red shrink-0">2.</span>
                    <span>Record the <strong>smallest</strong> of the three</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-bold text-primary-red shrink-0">3.</span>
                    <span>Measure to the nearest <strong>1/8&quot;</strong></span>
                  </li>
                </ol>
              </div>
            </div>

            {/* Important Note */}
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm">
              <svg className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-amber-800">
                <strong>Do not make deductions.</strong> Provide exact measurements — we&apos;ll make standard deductions for proper fit.
              </p>
            </div>
          </div>
        )}

        {/* Outside Mount Content */}
        {mountType === 'outside' && (
          <div className="space-y-6">
            {/* When to use */}
            <div className="bg-rose-50 border border-rose-100 rounded-lg p-5">
              <h3 className="font-semibold text-rose-900 mb-2">Best for</h3>
              <p className="text-rose-800 text-sm">
                Shallow frames, windows with obstructions (handles, cranks), or when you want windows to appear larger.
              </p>
            </div>

            {/* Measurement Image */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-center mb-4">How to Measure — Outside Mount</h3>
              <img
                src="/images/guides/inside-outside-mount.jpg"
                alt="Outside mount measurement guide — black lines show width, red lines show height"
                className="w-full max-w-4xl mx-auto h-auto rounded-lg mb-4"
              />
              <div className="flex justify-center gap-6 mb-4">
                <span className="flex items-center gap-2 text-sm">
                  <span className="w-8 h-0.5 bg-black inline-block"></span>
                  <strong>Black lines</strong> = Width
                </span>
                <span className="flex items-center gap-2 text-sm">
                  <span className="w-0.5 h-5 bg-red-500 inline-block"></span>
                  <strong className="text-red-600">Red lines</strong> = Height
                </span>
              </div>
              <div className="bg-rose-50 rounded-lg p-4 text-sm text-rose-900">
                <p className="font-semibold mb-1">The smallest measurement wins:</p>
                <ul className="space-y-1 ml-4 list-disc">
                  <li>The <strong>lowest value among the black lines</strong> is your <strong>blind width</strong></li>
                  <li>The <strong>lowest value among the red lines</strong> is your <strong>blind height</strong></li>
                </ul>
              </div>
            </div>

            {/* Steps */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Width */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-8 h-8 bg-rose-600 text-white rounded-full flex items-center justify-center font-bold text-sm">W</span>
                  <h3 className="text-lg font-bold">Width <span className="text-sm font-normal text-gray-500">(black lines)</span></h3>
                </div>
                <ol className="space-y-3 text-sm">
                  <li className="flex gap-3">
                    <span className="font-bold text-rose-600 shrink-0">1.</span>
                    <span>Measure the area you want to cover at <strong>top, middle, and bottom</strong></span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-bold text-rose-600 shrink-0">2.</span>
                    <span>Record the <strong>smallest</strong> of the three</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-bold text-rose-600 shrink-0">3.</span>
                    <span>Add <strong>3–4 inches per side</strong> for light coverage</span>
                  </li>
                </ol>
              </div>

              {/* Height */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-8 h-8 bg-rose-600 text-white rounded-full flex items-center justify-center font-bold text-sm">H</span>
                  <h3 className="text-lg font-bold">Height <span className="text-sm font-normal text-gray-500">(red lines)</span></h3>
                </div>
                <ol className="space-y-3 text-sm">
                  <li className="flex gap-3">
                    <span className="font-bold text-rose-600 shrink-0">1.</span>
                    <span>Start <strong>3–4 inches above</strong> the window</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-bold text-rose-600 shrink-0">2.</span>
                    <span>Measure down to the sill or below</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-bold text-rose-600 shrink-0">3.</span>
                    <span>Record the <strong>total height</strong> you want</span>
                  </li>
                </ol>
              </div>
            </div>

            {/* Important Note */}
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm">
              <svg className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-amber-800">
                <strong>We manufacture to your exact size.</strong> Include any overlap you want in your measurements.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Quick Comparison */}
      <div className="mb-10">
        <h2 className="text-xl font-bold mb-4">Quick Comparison</h2>
        <div className="overflow-hidden rounded-lg border border-gray-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left p-3 font-semibold"></th>
                <th className="text-center p-3 font-semibold text-primary-red">Inside Mount</th>
                <th className="text-center p-3 font-semibold text-rose-700">Outside Mount</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-gray-100">
                <td className="p-3 font-medium">Width rule</td>
                <td className="p-3 text-center">Use <strong>smallest</strong> of 3</td>
                <td className="p-3 text-center">Add 3–4" per side</td>
              </tr>
              <tr className="border-t border-gray-100 bg-gray-50/50">
                <td className="p-3 font-medium">Height rule</td>
                <td className="p-3 text-center">Use <strong>smallest</strong> of 3</td>
                <td className="p-3 text-center">Add 3–4" above window</td>
              </tr>
              <tr className="border-t border-gray-100">
                <td className="p-3 font-medium">Min depth needed</td>
                <td className="p-3 text-center">2.5"</td>
                <td className="p-3 text-center">No minimum</td>
              </tr>
              <tr className="border-t border-gray-100 bg-gray-50/50">
                <td className="p-3 font-medium">Deductions</td>
                <td className="p-3 text-center">We handle it</td>
                <td className="p-3 text-center">You set exact size</td>
              </tr>
              <tr className="border-t border-gray-100">
                <td className="p-3 font-medium">Look</td>
                <td className="p-3 text-center">Clean, built-in</td>
                <td className="p-3 text-center">Bold, full coverage</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Pro Tips */}
      <div className="mb-10 bg-gray-50 rounded-lg p-6">
        <h2 className="text-xl font-bold mb-3">Pro Tips</h2>
        <ul className="space-y-2 text-sm text-gray-700">
          <li className="flex items-start gap-2">
            <svg className="w-4 h-4 text-green-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            Measure each window individually — even identical-looking windows can vary
          </li>
          <li className="flex items-start gap-2">
            <svg className="w-4 h-4 text-green-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            Always use a steel tape measure — cloth tapes stretch
          </li>
          <li className="flex items-start gap-2">
            <svg className="w-4 h-4 text-green-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            Measure everything twice before ordering
          </li>
        </ul>
      </div>

      {/* CTA */}
      <div className="bg-gray-100 rounded-lg p-8 text-center">
        <h2 className="text-2xl font-bold mb-2">Need Help Measuring?</h2>
        <p className="text-gray-600 mb-6">
          Book a free professional measuring appointment — we'll come to you.
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <Link
            href="/consultation"
            className="bg-primary-red hover:bg-primary-red-dark text-white font-medium py-3 px-6 rounded-lg transition-colors"
          >
            Book Free Consultation
          </Link>
          <Link
            href="/contact"
            className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-3 px-6 rounded-lg transition-colors"
          >
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
}
